// Maps D365 membership status strings to Alert presentation props (type / urgency).
// Copy (title + description) is supplied exclusively from appsettings via the
// MemberAreaContainer DTO `statusAlerts` — no hardcoded fallback wording.

const MEMBER_STATUS_ALERT_CONFIG = {
  'current - pending approval': {
    overrideKey: 'pendingApproval',
    type: 'warning',
    urgent: false,
    dismissible: false,
    renewalJourney: true,
  },
  'pending approval': {
    overrideKey: 'pendingApproval',
    type: 'warning',
    urgent: false,
    dismissible: false,
  },
  'current - pending payment': {
    overrideKey: 'pendingPayment',
    type: 'warning',
    urgent: true,
    dismissible: false,
    renewalJourney: true,
  },
  // New application awaiting payment (registration, 803750001) — plain "Pending Payment".
  // Lands on the same invoice page as a renewal, but shows first-membership wording.
  'pending payment': {
    overrideKey: 'newApplicationPayment',
    type: 'warning',
    urgent: true,
    dismissible: false,
  },
  'current - pending renewal': {
    overrideKey: 'pendingRenewal',
    type: 'warning',
    urgent: false,
    dismissible: false,
    renewalJourney: true,
  },
  lapsed: {
    overrideKey: 'lapsed',
    type: 'error',
    urgent: true,
    dismissible: false,
  },
}

const nonEmpty = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null)

/**
 * Returns Alert props for the given D365 membership status string,
 * or null if the status has no alert or appsettings copy is missing.
 *
 * @param {string|null|undefined} status
 * @param {object|null|undefined} statusAlerts - CrmMember:StatusAlerts via MemberAreaContainer DTO
 * @param {boolean} [isPrivileged=false] - true for Main/Administrator/Finance roles. Renewal-journey
 *   banners are fail-closed: they are suppressed unless the role is a known privileged role.
 * @returns {{ type: string, urgent: boolean, dismissible: boolean, title: string, description: string } | null}
 */
export const getMemberStatusAlertProps = (status, statusAlerts, isPrivileged = false) => {
  if (!status || !statusAlerts) return null

  const base = MEMBER_STATUS_ALERT_CONFIG[status.toLowerCase().trim()]
  if (!base) return null

  // Renewal-journey banners are purely gated to privileged roles (fail-closed).
  if (base.renewalJourney && !isPrivileged) return null

  const override = statusAlerts[base.overrideKey]
  const title = nonEmpty(override?.title)
  const description = nonEmpty(override?.description)
  if (!title || !description) return null

  const { overrideKey, renewalJourney, ...props } = base
  return { ...props, title, description }
}
